import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/useToast';
import {
  getInventorySettings,
  saveInventorySettings,
  resetInventorySettings,
  type InventorySettings,
  type DecimalRoundingMode,
  type StockViewPreference,
  DECIMAL_DIGIT_OPTIONS,
  ROUNDING_OPTIONS,
  STOCK_VIEW_OPTIONS,
  PERCENTAGE_OPTIONS,
} from '@/lib/settings';
import {
  RotateCcw,
  Save,
  Info,
  CheckCircle,
  Package,
  Calculator,
  Bell,
} from 'lucide-react';

interface SettingsInventoryTabProps {
  onSave?: () => void;
}

export default function SettingsInventoryTab({ onSave }: SettingsInventoryTabProps) {
  const [settings, setSettings] = useState<InventorySettings>(getInventorySettings);
  const [saved, setSaved] = useState(false);
  const { success: toastSuccess } = useToast();

  useEffect(() => {
    setSettings(getInventorySettings());
  }, []);

  const handleSave = () => {
    saveInventorySettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    toastSuccess('Settings saved', 'Inventory settings have been updated.');
    onSave?.();
  };

  const handleReset = () => {
    setSettings(resetInventorySettings());
    toastSuccess('Settings reset', 'Inventory settings have been reset to defaults.');
  };

  const updateSetting = <K extends keyof InventorySettings>(
    key: K,
    value: InventorySettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Decimal Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Decimal & Rounding
          </CardTitle>
          <CardDescription>
            Configure how quantities and calculations are displayed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="decimalDigits">Decimal Digits</Label>
              <select
                id="decimalDigits"
                value={settings.decimalDigits}
                onChange={(e) => updateSetting('decimalDigits', Number(e.target.value))}
                className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary w-full"
              >
                {DECIMAL_DIGIT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-text-muted">
                Number of decimal places to display
              </p>
            </div>

            <div className="space-y-2">
              <Label>Rounding Mode</Label>
              <div className="space-y-2">
                {ROUNDING_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      settings.decimalRounding === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="rounding"
                      value={opt.value}
                      checked={settings.decimalRounding === opt.value}
                      onChange={() => updateSetting('decimalRounding', opt.value as DecimalRoundingMode)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{opt.label}</p>
                      <p className="text-xs text-text-muted">{opt.description}</p>
                    </div>
                    {settings.decimalRounding === opt.value && (
                      <CheckCircle className="h-4 w-4 text-primary" />
                    )}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stock Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Stock Alerts
          </CardTitle>
          <CardDescription>
            Configure stock level alerts and validation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="allowNegativeStock" className="text-base">
                Allow Negative Stock
              </Label>
              <p className="text-sm text-text-muted">
                Allow stock to go below zero (backorder)
              </p>
            </div>
            <Switch
              id="allowNegativeStock"
              checked={settings.allowNegativeStock}
              onCheckedChange={(checked) => updateSetting('allowNegativeStock', checked)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="alertPercent">Low Stock Alert Threshold</Label>
            <select
              id="alertPercent"
              value={settings.minimumStockAlertPercent}
              onChange={(e) => updateSetting('minimumStockAlertPercent', Number(e.target.value))}
              className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary w-full md:w-64"
            >
              {PERCENTAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} of minimum stock
                </option>
              ))}
            </select>
            <p className="text-xs text-text-muted">
              Alert when stock falls below this percentage of minimum stock level
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Display Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Display Preferences
          </CardTitle>
          <CardDescription>
            How inventory data is displayed throughout the application
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Default Stock View</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {STOCK_VIEW_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                    settings.defaultStockView === opt.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="stockView"
                    value={opt.value}
                    checked={settings.defaultStockView === opt.value}
                    onChange={() => updateSetting('defaultStockView', opt.value as StockViewPreference)}
                    className="sr-only"
                  />
                  <div className="flex-1">
                    <p className="font-medium">{opt.label}</p>
                  </div>
                  {settings.defaultStockView === opt.value && (
                    <CheckCircle className="h-4 w-4 text-primary" />
                  )}
                </label>
              ))}
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mt-2">
              <div className="flex gap-2">
                <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Smart Display</p>
                  <p className="text-blue-700">
                    Automatically converts to the most readable unit. Example: 500 pcs → 1 rim (if rim = 500 pcs)
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="autoSuggestConversion" className="text-base">
                Auto-Suggest Conversions
              </Label>
              <p className="text-sm text-text-muted">
                Suggest optimal units when creating transfers or issues
              </p>
            </div>
            <Switch
              id="autoSuggestConversion"
              checked={settings.autoSuggestConversion}
              onCheckedChange={(checked) => updateSetting('autoSuggestConversion', checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="showStockValue" className="text-base">
                Show Stock Value
              </Label>
              <p className="text-sm text-text-muted">
                Display total value (qty × cost) in inventory views
              </p>
            </div>
            <Switch
              id="showStockValue"
              checked={settings.showStockValue}
              onCheckedChange={(checked) => updateSetting('showStockValue', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleReset}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset to Defaults
        </Button>
        <Button onClick={handleSave}>
          <Save className="mr-2 h-4 w-4" />
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      {/* Info */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm text-text-secondary">
              <p className="font-medium text-text-primary mb-1">Settings are stored locally</p>
              <p>
                These settings are saved in your browser and will persist across sessions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
